// Clinic contact shown in the shell sidebar header.
//
// The clinic's address / phone / email live in the singleton `app_config` row
// (columns `org_address`, `org_phone`, `org_mobile`, `org_email`) and are edited
// by admins in Clinic Settings. The shell previously read the build-time
// `VITE_SUPPORT_*` env vars instead, which describe the *platform support*
// contact rather than the clinic's own — and are baked in at build time, so a
// clinic could not correct them without a rebuild and redeploy.
//
// Clinic Settings therefore wins, with the env values kept as a fallback so
// orgs that have not filled the form in yet keep their current sidebar.
//
// This module is intentionally self-contained (no workspace-package imports) so
// it can run under the node:test harness. The `app_config` row is accepted via
// a structural shape that AppConfigRow satisfies.

/** Structural shape of the app_config columns this module reads. */
export interface ClinicContactSource {
  org_address?: string | null;
  org_phone?: string | null;
  org_mobile?: string | null;
  org_email?: string | null;
}

/** Resolved contact details. Missing values are '' so callers can omit the row. */
export interface ClinicContact {
  address: string;
  phone: string;
  email: string;
}

/**
 * Build-time fallbacks (the `VITE_SUPPORT_*` env vars). Used only where Clinic
 * Settings has left a field blank.
 */
export interface ClinicContactFallback {
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return '';
}

/**
 * Resolve the clinic's contact details for display, preferring what the clinic
 * admin saved in Clinic Settings over the build-time env fallback. Values are
 * live and per-org, so editing Clinic Settings updates the shell without a
 * rebuild.
 *
 * `phone` falls back to `org_mobile` before reaching the env value: a clinic
 * that filled in only Mobile still shows its own number rather than the
 * platform support line.
 */
export function getClinicContact(
  row: ClinicContactSource | null | undefined,
  fallback: ClinicContactFallback = {},
): ClinicContact {
  return {
    address: firstNonEmpty(row?.org_address, fallback.address),
    phone: firstNonEmpty(row?.org_phone, row?.org_mobile, fallback.phone),
    email: firstNonEmpty(row?.org_email, fallback.email),
  };
}
